/* eslint-disable react/prop-types */

/**
 * Special onEnter function to redirect all
 * of the external routes.
 */
import React from 'react'
import { Redirect } from 'react-router-dom'
import { batch } from 'react-redux' // eslint-disable-line no-restricted-imports
import * as ViewActions from '../actions/Actions'
import store from '../stores/configureStore'
import * as SendActions from '../actions/send'
import Events from 'lib/analytics/eventTags'
import { amountIsZero } from 'lib/amountUtils'
import { parseSearch, safeParseWindowSearch } from './url-utils'
import {
  ANALYTICS_FLOW_TYPE,
  ENV_TYPE,
  PAYMENT_TYPE,
  SUB_FLOW,
  TOAST_DURATION,
  TOAST_TYPES,
} from '../../../../lib/constants'
import { OPEN_BANKING_FLOWS } from '../constants'
import serverProps from './serverProps'
import logger from 'loglevel'
import { isOpenBankingTreatment } from '../../experiments/openBanking'
import { tracking } from '../../lib/analytics/mParticle'
import {
  trackServiceAndClientDecision,
  updateSelectedPurposeOfCode,
} from '../actions/unified-actions'
import { getMainWindow } from '../../lib/iframeUtils'
import { isFeeTransparencyUpliftTrmt } from '../../experiments/feeTransparencyUplift'
import { isCrossCurrencyEnabled } from './currencyUtils'
import { showGlobalToast } from '../actions/global-toast'
import content from 'pp-react-l10n'
import { isMoneyMovementTrmt } from '../../experiments/easeOfPaymentsFeatures'
const i18nPreview = content('transfer/page/preview')

const flowMap = {
  [PAYMENT_TYPE.PERSONAL]: 'send',
  [PAYMENT_TYPE.PURCHASE]: 'buy',
  [PAYMENT_TYPE.DONATION]: 'buy',
}

// prettier-ignore
/* eslint-disable max-statements */
export default function onEnter(props) { // NOSONAR
  let { env, type } = props.match.params
  if (props.match.url.includes('payRequest')) {
    env = ENV_TYPE.PAY_REQUEST
  }
  const state = store.getState()
  const {
    fundingOptionsData,
    obFlow,
    analyticsFlow,
    flowType,
    senderCipContingencyRequired = false,
    selectedPurposeOfPaymentOption = {},
    memberCreatedFromGuest = ''
  } = serverProps
  let {
    server: {
      rootURL,
      groupFeatures: { shouldShowSelectFundingOptionsPage = false },
    },
    recipient: {
      suggestedPaymentType,
      isServiceSupportedExternalFlow,
      cipFlow,
      serviceContext,
    },
    transaction: { paymentTypeIsSelected } = {},
  } = state

  // Flow type is restored from session for non-initial external entry points.
  // We must preserve it even if suggestedPaymentType is defined, except when
  // :type URL param exists for '/:type/external/:env' which takes precedence.
  if (!type && flowType) {
    type = flowType
  }

  // capture external params from url
  const mainWindow = getMainWindow()
  const params = {
    ...safeParseWindowSearch(mainWindow),
    ...parseSearch(props.location.search),
    env,
    type,
    cipFlow,
  }
  const {
    success: base64SuccessfullyAddedFundingInstrumentIds = '',
    error: base64ErrorFlow = '',
  } = params

  const isOpenBankingReconsentFlow =
    isOpenBankingTreatment() &&
    obFlow === OPEN_BANKING_FLOWS.CONSENT_CONTINGENCY

  let successfullyAddedFundingInstrumentIds = ''
  if (
    base64SuccessfullyAddedFundingInstrumentIds &&
    /**
     * Some external flows (such as Negative Balance) use the success parameter
     * as a string value boolean. But the success flag is used to autoselect
     * a funding instrument on a plan for when resolving contingencies.
     *
     * As there is no future-proof & scalable way to detect for valid funding
     * instrument ID, we need to fall back to a list of omited values.
     *
     * dHJ1ZQ = true
     *
     * If we need to add more, simply: /dHJ1ZQ|whatever|etc/
     */
    !/dHJ1ZQ/.test(base64SuccessfullyAddedFundingInstrumentIds)
  ) {
    try {
      // window.atob() method decodes a base-64 encoded string.
      successfullyAddedFundingInstrumentIds = atob(
        base64SuccessfullyAddedFundingInstrumentIds
      )
    } catch (e) {
      // There was an error parsing the FI id returned by wallet
      // If this happen, we continue with the regular flow because we can not auto select that FI
      logger.error(e)
    }
  }

  tracking(
    Events.p2p_external_entry_occurred({
      flow_from: env || 'unknown',
      http_params: JSON.stringify({
        ...params,
        ...(obFlow && { obFlow }),
        ...(successfullyAddedFundingInstrumentIds && {
          fi_id: successfullyAddedFundingInstrumentIds,
        }),
      }),
    })
  )

  trackServiceAndClientDecision({ serviceContext, suggestedPaymentType })

  const isCrossCurrencyEnabledFlow = isCrossCurrencyEnabled({
    amount: store.getState().amount,
    toCurrencyCode: store.getState().amount.currencyCode,
    fromCurrencyCode: store.getState().crossBorder.fromCurrencyCode
  })

  if((isMoneyMovementTrmt() && suggestedPaymentType === PAYMENT_TYPE.UNKNOWN && isCrossCurrencyEnabledFlow) ||
      (isFeeTransparencyUpliftTrmt() && isCrossCurrencyEnabledFlow)
    ) {
    store.dispatch(
      ViewActions.initFXCalculator(
        store.getState().amount.amount,
        store.getState().amount.currencyCode,
        {
          fromCurrencyCode: store.getState().crossBorder.fromCurrencyCode,
          isFromExternalRedirect: true,
          fromAmount: store.getState().crossBorder.originAmount,
        }
      )
    )
  }

  // url honoring of type
  if (
    analyticsFlow !== ANALYTICS_FLOW_TYPE.ACTIVITY &&
    type &&
    !isServiceSupportedExternalFlow
  ) {
    if (type === 'send') {
      suggestedPaymentType = PAYMENT_TYPE.PERSONAL
    } else if (type === 'buy') {
      suggestedPaymentType = PAYMENT_TYPE.PURCHASE
    }
  }

  // some apps send data via query params
  if (cipFlow || state.external.captureQueryParams) {
    store.dispatch(ViewActions.captureExternalParams(params))
  }

  /**
   * Created a separate Action to capture environment specific params
   * after the redirection is complete and flow returns back to p2p external flow
   */
  store.dispatch(ViewActions.captureExternalIntegrationParams(params))

  // trigger the external initialize hook
  store.dispatch(ViewActions.triggerExternalHook('initialize', state))
  // treat DONATION as PURCHASE so that we don't break existing tracking by this change.
  if (!paymentTypeIsSelected) {
    store.dispatch(
      ViewActions.changeIsPurchase(
        [PAYMENT_TYPE.PURCHASE, PAYMENT_TYPE.DONATION].includes(
          suggestedPaymentType
        )
      )
    )
  }

  if (isServiceSupportedExternalFlow) {
    store.dispatch(
      ViewActions.setServiceReturnedPaymentType(suggestedPaymentType)
    )
  }

  if (successfullyAddedFundingInstrumentIds) {
    store.dispatch(
      SendActions.saveSuccessfullyAddedFundingInstrumentIds(
        successfullyAddedFundingInstrumentIds
      )
    )
  }

  if (senderCipContingencyRequired) {
    store.dispatch(SendActions.updateSenderReturningFromCIP(true))
  }

  // Triggering to update the purpose code.
  if (selectedPurposeOfPaymentOption) {
    store.dispatch(updateSelectedPurposeOfCode(selectedPurposeOfPaymentOption))
  }

  if (isOpenBankingReconsentFlow) {
    const ignorePreferred = true
    batch(() => {
      if (base64ErrorFlow) {
        store.dispatch(SendActions.setOpenBankingError(base64ErrorFlow))
      }
      store.dispatch(
        SendActions.receivedFundingOptions(
          fundingOptionsData,
          shouldShowSelectFundingOptionsPage,
          ignorePreferred
        )
      )
    })
  } else if (
    (!isMoneyMovementTrmt() || suggestedPaymentType !== PAYMENT_TYPE.UNKNOWN) &&
    // do not fetch funding options for payRequest & hawk-ppcp
    env !== ENV_TYPE.HAWK_PPCP &&
    env !== ENV_TYPE.PAY_REQUEST &&
    !amountIsZero(store.getState().amount.amount) &&
    (state.external.action !== 'request' || state.external.action !== 'split')
  ) {
    let instrumentIds = []
    if (
      successfullyAddedFundingInstrumentIds &&
      typeof successfullyAddedFundingInstrumentIds === 'string'
    ) {
      instrumentIds = successfullyAddedFundingInstrumentIds.split(',')
    }
    // immediately start looking for funding options if not request route
    // checking amount because fetchFundingOptions requires valid amount
    // NOTE: use store.getState() here to get the *latest* amount
    // Update: Do not fetch funding option here if it's 'hawk-ppcp' case, since it's an external route without knowing payment type
    // FI will be fetched after user choose payment type
    store.dispatch(SendActions.fetchFundingOptions({ instrumentIds }))
  }

  if (env === ENV_TYPE.PAY_REQUEST) {
    const { requestId, requestLegId } = props.match.params
    return (
      <Redirect to={`${rootURL}/payRequest/${requestId}/${requestLegId}`} />
    )
  }

  if (memberCreatedFromGuest === 'success') {
    store.dispatch(
      showGlobalToast({
        type: TOAST_TYPES.SUCCESS,
        message: i18nPreview('toast.memberCreationFromGuestSuccess'),
        duration: TOAST_DURATION,
      })
    )
    // In the future we can track when coming from pay a request as guest flow 
    tracking(
      Events.p2p_review_account_creation_success_toast_shown({
        sub_flow: SUB_FLOW.GROUPS_GUEST,
      })
    )
  }

  if (
    suggestedPaymentType === PAYMENT_TYPE.PURCHASE ||
    suggestedPaymentType === PAYMENT_TYPE.PERSONAL ||
    suggestedPaymentType === PAYMENT_TYPE.DONATION
  ) {
    return (
      <Redirect
        to={`${rootURL}/homepage/${flowMap[suggestedPaymentType]}/preview`}
      />
    )
  }

  // otherwise go to preview page but ask to show the payment type overpanel first
  return <Redirect to={`${rootURL}/homepage/preview`} />
}
